import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import { apiErrorMessage } from '../api';
import { help, HelpText } from '../helpTexts';
import { cardSx, chipButtonSx, launchColors, overlineSx, panelSx } from '../launchTheme';
import { dateTimeText, eanChecksumOk, personName } from '../productInfo/productLabels';
import AssetHoverLink from '../ui/AssetHoverLink';
import CopyButton, { copyText, labeledText } from '../ui/CopyButton';
import InfoTip from '../ui/InfoTip';
import ProductImage from '../ui/ProductImage';
import ProgressRing from '../ui/ProgressRing';
import SaveIndicator, { saveStamp, SaveState } from '../ui/SaveIndicator';
import { StatusPill } from '../ui/StatusPill';
import { alcoholApi } from './alcoholApi';
import { alcoholCommandId, categoryTone, counterColor, pill, ringColor, STATE_TONES, STATUS_TONES } from './alcoholLabels';
import {
  AlcoholDetail,
  AlcoholDuplicate,
  AlcoholFacets,
  AlcoholFieldDef,
  AlcoholHistoryEntry,
  AlcoholPrompts,
  AlcoholState,
  NAME_LIMIT,
  PAIRINGS,
  PROMPT_LABELS,
  PromptKey,
  SHORT_LIMIT,
  STATE_LABELS,
  STATES,
  STATUS_LABELS,
  SUSTAINABILITY,
  WINERY_LIMIT,
} from './alcoholTypes';
import GeneratedTextField from './GeneratedTextField';
import TextHistoryDialog from './TextHistoryDialog';

interface Props {
  productId: string;
  fields: AlcoholFieldDef[];
  facets: AlcoholFacets;
  prompts: AlcoholPrompts | null;
  canEdit: boolean;
  names: Record<string, string>;
  athenaAssetId?: string;
  onChanged: () => void;
  onBack?: () => void;
  onOpen: (productId: string) => void;
  onUnsaved?: (labels: string[]) => void;
}

type Patch = Record<string, unknown>;
type Suggestions = Partial<Record<PromptKey, string>>;
type HistoryAsk = { title: string; label: string; current: string; load: () => Promise<AlcoholHistoryEntry[]>; restore: (text: string) => void };

const BASIC = ['name', 'country', 'category', 'packaging', 'alcohol', 'ean'];
const WINE = ['region', 'grape', 'soil'];
const RANGE = ['assortment', 'systembolagetNumber'];

const Block: React.FC<{ title: string; info?: HelpText; id?: string; children: React.ReactNode }> = ({ title, info, id, children }) => (
  <Box id={id} component="section" aria-label={title} tabIndex={-1} sx={[panelSx, { scrollMarginTop: 88, '&:focus': { outline: 'none' } }]}>
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
      <Typography component="h3" sx={overlineSx}>{title}</Typography>
      {info && <InfoTip text={info} />}
    </Stack>
    {children}
  </Box>
);

const Fold: React.FC<{ title: string; id?: string; defaultExpanded?: boolean; onExpand?: () => void; children: React.ReactNode }> = ({ title, id, defaultExpanded, onExpand, children }) => (
  <Accordion id={id} tabIndex={-1}
    defaultExpanded={defaultExpanded}
    disableGutters
    TransitionProps={{ unmountOnExit: true }}
    onChange={(_event, expanded) => { if (expanded) onExpand?.(); }}
    sx={{ boxShadow: 'none', border: `1px solid ${launchColors.divider}`, borderRadius: '12px !important', '&::before': { display: 'none' } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, color: launchColors.navy }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const FieldEditor: React.FC<{
  field: AlcoholFieldDef;
  value: string;
  options?: string[];
  canEdit: boolean;
  onSave: (value: string) => void;
  onTooLong?: (tooLong: boolean) => void;
  onCopied: (ok: boolean) => void;
}> = ({ field, value, options, canEdit, onSave, onTooLong, onCopied }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const tooLong = draft.length > field.maxLength;
  const onTooLongRef = useRef(onTooLong);
  onTooLongRef.current = onTooLong;
  useEffect(() => { onTooLongRef.current?.(tooLong); }, [tooLong]);
  const badEan = field.kind === 'ean' && Boolean(draft) && /^\d+$/.test(draft) && !eanChecksumOk(draft);
  const counted = field.key === 'name';
  const helper = [counted ? `${draft.length}/${field.maxLength}` : '', badEan ? 'Fel kontrollsiffra' : '', tooLong && !counted ? `Högst ${field.maxLength} tecken` : ''].filter(Boolean).join(' · ');
  const commit = (next: string) => {
    const trimmed = next.trim();
    if (!canEdit || trimmed === value || trimmed.length > field.maxLength) return;
    onSave(trimmed);
  };
  const helperSx = { '& .MuiFormHelperText-root': counted ? { color: counterColor(draft.length, field.maxLength), fontWeight: tooLong ? 700 : 400 } : {} };
  const copyButton = <CopyButton label={field.label} text={draft} onCopied={onCopied} />;
  if (options && canEdit) {
    return (
      <Box id={`field-${field.key}`}>
        <Autocomplete
          freeSolo
          options={options}
          inputValue={draft}
          onInputChange={(_event, next, reason) => { if (reason !== 'reset') setDraft(next); }}
          onChange={(_event, next) => { if (typeof next === 'string') commit(next); }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={field.label}
              onBlur={() => commit(draft)}
              error={tooLong}
              helperText={helper || undefined}
              InputProps={{ ...params.InputProps, endAdornment: <>{copyButton}{params.InputProps.endAdornment}</> }}
              sx={{ bgcolor: launchColors.card }}
            />
          )}
        />
      </Box>
    );
  }
  return (
    <Box id={`field-${field.key}`}>
    <TextField
      size="small"
      fullWidth
      label={field.label}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDraft(value);
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
      error={tooLong || badEan}
      helperText={helper || undefined}
      InputProps={{
        readOnly: !canEdit,
        endAdornment: (
          <InputAdornment position="end">
            {field.key === 'alcohol' ? '%' : null}
            {copyButton}
          </InputAdornment>
        ),
      }}
      sx={[{ bgcolor: launchColors.card }, helperSx, !canEdit ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: launchColors.divider } } : {}]}
    />
    </Box>
  );
};

const MISSING_TARGET: Record<string, string> = {
  Produktnamn: 'field-name',
  Land: 'field-country',
  Kategori: 'field-category',
  Förpackning: 'field-packaging',
  Alkoholhalt: 'field-alcohol',
  Region: 'field-region',
  Beskrivning: 'field-description',
  'Kortare beskrivning': 'field-shortDescription',
  'Passar till': 'passar',
  'Beskrivning vingård': 'field-winery',
};

const focusTarget = (id: string) => {
  const node = document.getElementById(id);
  if (!node) return;
  node.scrollIntoView({ block: 'center' });
  const input = node.querySelector('input, textarea');
  if (input instanceof HTMLElement) input.focus();
  else node.focus();
};

const clipText = (value: string, full: boolean) => (full || value.length <= 160 ? value : `${value.slice(0, 160)}…`);

const AlcoholCard: React.FC<Props> = ({ productId, fields, facets, prompts, canEdit, names, athenaAssetId, onChanged, onBack, onOpen, onUnsaved }) => {
  const [detail, setDetail] = useState<AlcoholDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [duplicateAsk, setDuplicateAsk] = useState<{ patch: Patch; duplicates: AlcoholDuplicate[] } | null>(null);
  const [history, setHistory] = useState<AlcoholHistoryEntry[] | null>(null);
  const [note, setNote] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [suggestions, setSuggestions] = useState<Suggestions>({});
  const [shortNote, setShortNote] = useState('');
  const [generating, setGenerating] = useState<PromptKey | null>(null);
  const [historyAsk, setHistoryAsk] = useState<HistoryAsk | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState('');
  const [expandedChanges, setExpandedChanges] = useState<string[]>([]);
  const blocked = useRef<Record<string, string>>({});
  const onUnsavedRef = useRef(onUnsaved);
  onUnsavedRef.current = onUnsaved;
  const noteTooLong = useCallback((key: string, label: string, tooLong: boolean) => {
    if (tooLong) blocked.current[key] = label;
    else delete blocked.current[key];
    onUnsavedRef.current?.(Object.values(blocked.current));
  }, []);

  const load = useCallback(() => {
    setError(null);
    alcoholApi.item(productId)
      .then((next) => { setDetail(next); setHistory(null); })
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Produkten kunde inte hämtas.')));
  }, [productId]);

  useEffect(() => {
    setDetail(null);
    setNotice(null);
    setSuggestions({});
    setShortNote('');
    setSaveState('idle');
    blocked.current = {};
    onUnsavedRef.current?.([]);
    load();
  }, [load]);

  const run = async (command: Patch, allowDuplicate = false): Promise<boolean> => {
    if (!detail) return false;
    setSaving(true);
    setSaveState('saving');
    setError(null);
    try {
      const next = await alcoholApi.command(productId, {
        commandId: alcoholCommandId(),
        expectedVersion: detail.product.version,
        ...command,
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      });
      setDetail(next);
      setHistory(null);
      setSaveState('saved');
      setSavedAt(saveStamp());
      onChanged();
      return true;
    } catch (requestError: any) {
      setSaveState('error');
      setResetKey((value) => value + 1);
      const data = requestError?.response?.data;
      if (requestError?.response?.status === 409 && Array.isArray(data?.duplicates)) {
        setDuplicateAsk({ patch: command, duplicates: data.duplicates });
      } else if (requestError?.response?.status === 409) {
        setNotice('Någon annan hann ändra produkten. Kortet har laddats om.');
        load();
      } else {
        setError(apiErrorMessage(requestError, 'Ändringen kunde inte sparas.'));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = (patch: Patch) => { void run({ type: 'update', patch }); };
  const saveField = (key: string, value: string) => save({ fields: { [key]: value } });

  const saveWinery = async (text: string) => {
    const producer = detail?.producer;
    if (!producer) return;
    setSaving(true);
    setSaveState('saving');
    setError(null);
    try {
      await alcoholApi.producerCommand(producer.id, { commandId: alcoholCommandId(), expectedVersion: producer.version, wineryText: text });
      setSaveState('saved');
      setSavedAt(saveStamp());
      load();
      onChanged();
    } catch (requestError: any) {
      setSaveState('error');
      setResetKey((value) => value + 1);
      if (requestError?.response?.status === 409) {
        setNotice('Någon annan hann ändra vingårdstexten. Kortet har laddats om.');
        load();
      } else {
        setError(apiErrorMessage(requestError, 'Vingårdstexten kunde inte sparas.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const generate = async (target: PromptKey, text: string) => {
    if (!detail) return;
    setGenerating(target);
    setError(null);
    try {
      const result = target === 'winery' && detail.producer
        ? await alcoholApi.generateWinery(detail.producer.id, text)
        : await alcoholApi.generate(productId, target === 'description' ? 'description' : 'shortDescription', text);
      setSuggestions((current) => ({
        ...current,
        ...(result.description !== undefined ? { description: result.description } : {}),
        ...(result.shortDescription !== undefined ? { shortDescription: result.shortDescription } : {}),
        ...(result.wineryText !== undefined ? { winery: result.wineryText } : {}),
      }));
      if (result.shortDescription !== undefined) {
        setShortNote(result.shortTooLong ? `Förslaget blev längre än ${SHORT_LIMIT} tecken. Lägg det i rutan och korta det innan du sparar.` : '');
      }
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Texten kunde inte skapas.'));
    } finally {
      setGenerating(null);
    }
  };

  const copied = (ok: boolean) => setToast(ok ? 'Kopierat.' : 'Det gick inte att kopiera.');

  const dismiss = (target: PromptKey) => setSuggestions((current) => {
    const next = { ...current };
    delete next[target];
    return next;
  });

  if (!detail) {
    return (
      <Box sx={[cardSx, { p: 2 }]}>
        {error ? <Alert severity="error">{error}</Alert> : <LinearProgress />}
      </Box>
    );
  }

  const { product, completeness, warnings, yearbook, producer } = detail;
  const fieldOf = (key: string) => fields.find((field) => field.key === key);
  const statusTone = STATUS_TONES[product.status];
  const stateTone = STATE_TONES[product.state];
  const category = product.fields.category || '';
  const percent = completeness.total ? (completeness.done / completeness.total) * 100 : 0;
  const promptReady = (key: PromptKey) => Boolean(prompts?.[key]?.trim());
  const productHistory = () => alcoholApi.history(productId);
  const extra = Object.entries(product.extra);
  const answer = (value: string) => (value === 'yes' ? 'Ja' : value === 'no' ? 'Nej' : '');
  const allText = labeledText([
    ['Artikelnummer', product.articleNumber],
    ['Producent', product.producer],
    ['Status', STATUS_LABELS[product.status]],
    ['Läge', STATE_LABELS[product.state]],
    ...fields.map((field): [string, string] => [field.label, product.fields[field.key] || '']),
    [PROMPT_LABELS.winery, producer?.wineryText || ''],
    ['Passar till', PAIRINGS.filter((item) => product.pairings.includes(item.key)).map((item) => item.label).join(', ')],
    ...SUSTAINABILITY.map((item): [string, string] => [item.label, answer(product.sustainability[item.key])]),
  ]);
  const meta = [product.producer, product.fields.country].filter(Boolean).join(' · ');
  const approver = product.status === 'approved' ? personName(product.approvedBy, names) : '';
  const approval = product.status === 'approved'
    ? [approver ? `Godkänd av ${approver}` : '', product.approvedAt ? dateTimeText(product.approvedAt) : ''].filter(Boolean).join(' · ')
    : '';
  const options: Record<string, string[]> = {
    country: facets.countries.map((item) => item.name),
    category: facets.categories.map((item) => item.name),
    assortment: facets.assortments.map((item) => item.name),
  };
  const grid = (keys: string[]) => (
    <Box key={resetKey} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
      {keys.map((key) => {
        const field = fieldOf(key);
        if (!field) return null;
        return <FieldEditor key={key} field={field} value={product.fields[key] || ''} options={options[key]} canEdit={canEdit} onSave={(value) => saveField(key, value)} onTooLong={(tooLong) => noteTooLong(key, field.label, tooLong)} onCopied={copied} />;
      })}
    </Box>
  );
  const textField = (key: 'description' | 'shortDescription') => {
    const field = fieldOf(key);
    if (!field) return null;
    const current = product.fields[key] || '';
    return (
      <GeneratedTextField
        label={field.label}
        value={current}
        limit={field.maxLength}
        minRows={key === 'description' ? 5 : 3}
        canEdit={canEdit}
        promptReady={promptReady(key)}
        sourceText={key === 'shortDescription' ? product.fields.description || '' : undefined}
        generating={generating === key}
        suggestion={suggestions[key]}
        suggestionNote={key === 'shortDescription' ? shortNote : undefined}
        onGenerate={(draft) => { void generate(key, key === 'shortDescription' ? product.fields.description || '' : draft); }}
        onSave={(text) => saveField(key, text)}
        onUseSuggestion={(text) => { saveField(key, text); dismiss(key); }}
        onDismissSuggestion={() => dismiss(key)}
        onHistory={() => setHistoryAsk({ title: `Historik: ${field.label}`, label: field.label, current, load: productHistory, restore: (text) => saveField(key, text) })}
        onTooLong={(tooLong) => noteTooLong(key, field.label, tooLong)}
        onCopied={copied}
      />
    );
  };

  return (
    <Box component="section" aria-label={`Produkt ${product.articleNumber}`} sx={[cardSx, { p: { xs: 1, sm: 1.5 }, bgcolor: launchColors.section }]}>
      <Stack spacing={1.5}>
        <Box sx={panelSx}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {onBack && <IconButton aria-label="Tillbaka till listan" onClick={onBack} size="small"><ArrowBackIcon /></IconButton>}
            <ProductImage assetId={athenaAssetId} title={detail.name || product.articleNumber} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {category && <Box component="span" aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: categoryTone(category).dot, flexShrink: 0 }} />}
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.textSecondary }}>
                  {athenaAssetId
                    ? <AssetHoverLink assetId={athenaAssetId} title={detail.name || product.articleNumber} hasThumbnail>{product.articleNumber}</AssetHoverLink>
                    : product.articleNumber}
                  {meta ? ` · ${meta}` : ''}
                </Typography>
              </Stack>
              <Typography component="h2" title={detail.name || undefined} sx={{ fontSize: 20, fontWeight: 700, color: launchColors.navy, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {detail.name || 'Namn saknas'}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} useFlexGap flexWrap="wrap" alignItems="center">
                <StatusPill label={STATUS_LABELS[product.status]} {...pill(statusTone)} border={`1px solid ${statusTone.dot}`} />
                {product.state !== 'current' && <StatusPill label={STATE_LABELS[product.state]} {...pill(stateTone)} />}
                {category && <StatusPill label={category} {...pill(categoryTone(category))} />}
                {!canEdit && <StatusPill label="Bara läsning" fg={launchColors.textSecondary} bg={launchColors.neutralBg} border={`1px solid ${launchColors.divider}`} />}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, minHeight: 20 }}>
                {approval && <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{approval}</Typography>}
                <SaveIndicator state={saveState} savedAt={savedAt} />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack alignItems="center" spacing={0.25} title={completeness.missing.length ? `Saknas: ${completeness.missing.join(', ')}` : 'Allt är ifyllt'}>
                <ProgressRing value={percent} size={40} color={ringColor(completeness.done, completeness.total)} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{completeness.done}/{completeness.total}</Typography>
              </Stack>
              {canEdit && (product.status === 'coming' ? (
                <Button variant="contained" startIcon={<CheckIcon />} disabled={saving} onClick={() => { void run({ type: 'approve' }); }} sx={{ bgcolor: launchColors.mossDark, '&:hover': { bgcolor: launchColors.moss }, whiteSpace: 'nowrap' }}>
                  Godkänn
                </Button>
              ) : (
                <Button variant="outlined" startIcon={<ReplayIcon />} disabled={saving} onClick={() => { void run({ type: 'reopen' }); }} sx={{ whiteSpace: 'nowrap', bgcolor: launchColors.card }}>
                  Öppna igen
                </Button>
              ))}
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${launchColors.divider}` }}>
            <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
              {completeness.missing.length === 0 ? (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.mossDark }}>Allt är ifyllt</Typography>
              ) : (
                <>
                  <Typography component="span" sx={{ fontSize: 13, color: launchColors.textSecondary }}>Saknas:</Typography>
                  {completeness.missing.map((label, index) => (
                    <ButtonBase
                      key={label}
                      onClick={() => focusTarget(MISSING_TARGET[label] || 'grunddata')}
                      sx={{ font: 'inherit', fontSize: 13, color: launchColors.navy, textDecoration: 'underline', textDecorationColor: launchColors.divider, textUnderlineOffset: '3px', borderRadius: '4px', '&:hover': { textDecorationColor: launchColors.navy }, '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 } }}
                    >
                      {label}{index < completeness.missing.length - 1 ? ',' : ''}
                    </ButtonBase>
                  ))}
                </>
              )}
              <InfoTip text={help.alcoholComplete} />
            </Stack>
            <TextField
              select
              size="small"
              label="Läge"
              value={product.state}
              disabled={!canEdit || saving}
              onChange={(event) => save({ state: event.target.value as AlcoholState })}
              sx={{ minWidth: 170, bgcolor: launchColors.card }}
            >
              {STATES.map((state) => <MenuItem key={state} value={state}>{STATE_LABELS[state]}</MenuItem>)}
            </TextField>
            <Button size="small" startIcon={<ContentCopyIcon />} disabled={!allText} onClick={async () => copied(await copyText(allText))} sx={{ whiteSpace: 'nowrap' }}>
              Kopiera alla
            </Button>
          </Stack>
        </Box>
        {saving && <LinearProgress />}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}
        {warnings.filter((warning) => warning.kind === 'duplicate' || warning.kind === 'too_long' || warning.kind === 'ean_check').map((warning) => (
          <Alert key={`${warning.kind}-${'field' in warning ? warning.field : ''}`} severity="warning">
            {warning.message}
            {warning.kind === 'duplicate' && (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                {warning.others.map((other) => (
                  <Chip key={other.id} size="small" label={`${other.articleNumber} ${other.name}`.trim()} onClick={() => onOpen(other.id)} />
                ))}
              </Stack>
            )}
          </Alert>
        ))}

        <Block id="grunddata" title="Grunddata">
          <Box key={`basic-${resetKey}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, mb: 1.25 }}>
            <FieldEditor
              field={{ key: 'articleNumber', label: 'Artikelnummer', maxLength: 30 }}
              value={product.articleNumber}
              canEdit={canEdit}
              onSave={(value) => { if (value) save({ articleNumber: value }); }}
              onTooLong={(tooLong) => noteTooLong('articleNumber', 'Artikelnummer', tooLong)}
              onCopied={copied}
            />
            <FieldEditor
              field={{ key: 'producer', label: 'Producent', maxLength: 120 }}
              value={product.producer}
              options={facets.producers.map((item) => item.name)}
              canEdit={canEdit}
              onSave={(value) => save({ producer: value })}
              onTooLong={(tooLong) => noteTooLong('producer', 'Producent', tooLong)}
              onCopied={copied}
            />
          </Box>
          {grid(BASIC)}
          {(product.fields.name || '').length > NAME_LIMIT && (
            <Typography sx={{ fontSize: 12, color: launchColors.dangerText, mt: 0.5 }}>Produktnamnet är längre än {NAME_LIMIT} tecken.</Typography>
          )}
        </Block>

        <Block id="vinfakta" title="Vinfakta">{grid(WINE)}</Block>

        <Block id="texter" title="Texter" info={help.alcoholTexts}>
          <Stack spacing={2}>
            <Box id="field-description">{textField('description')}</Box>
            <Box id="field-shortDescription">{textField('shortDescription')}</Box>
            <Box id="field-winery" sx={{ p: 1.25, borderRadius: '10px', bgcolor: launchColors.section, border: `1px solid ${launchColors.divider}` }}>
              {producer ? (
                <>
                  <GeneratedTextField
                    label={PROMPT_LABELS.winery}
                    value={producer.wineryText}
                    limit={WINERY_LIMIT}
                    canEdit={canEdit}
                    promptReady={promptReady('winery')}
                    generating={generating === 'winery'}
                    suggestion={suggestions.winery}
                    onGenerate={(draft) => { void generate('winery', draft); }}
                    onSave={(text) => { void saveWinery(text); }}
                    onUseSuggestion={(text) => { void saveWinery(text); dismiss('winery'); }}
                    onDismissSuggestion={() => dismiss('winery')}
                    onHistory={() => setHistoryAsk({
                      title: `Historik: Beskrivning vingård, ${producer.name}`,
                      label: PROMPT_LABELS.winery,
                      current: producer.wineryText,
                      load: () => alcoholApi.producerHistory(producer.id),
                      restore: (text) => { void saveWinery(text); },
                    })}
                    onTooLong={(tooLong) => noteTooLong('winery', PROMPT_LABELS.winery, tooLong)}
                    onCopied={copied}
                  />
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, mt: 0.5 }}>
                    Gäller {producer.productCount === 1 ? 'den enda produkten' : `alla ${producer.productCount} produkter`} från {producer.name}.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Ange producent under Grunddata för att skriva Beskrivning vingård.</Typography>
              )}
            </Box>
          </Stack>
        </Block>

        <Block id="passar" title="Passar till">
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" role="group" aria-label="Passar till">
            {PAIRINGS.map((item) => {
              const active = product.pairings.includes(item.key);
              return (
                <Button
                  key={item.key}
                  aria-pressed={active}
                  disabled={!canEdit || saving}
                  startIcon={active ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
                  onClick={() => save({ pairings: active ? product.pairings.filter((key) => key !== item.key) : [...product.pairings, item.key] })}
                  sx={[chipButtonSx(active), { '&.Mui-disabled': { color: active ? '#FFFFFF' : launchColors.textSecondary, bgcolor: active ? launchColors.navyLight : launchColors.card } }]}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </Block>

        <Block id="sortiment" title="Sortiment och Systembolaget">{grid(RANGE)}</Block>

        <Block id="hallbarhet" title="Hållbarhet">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            {SUSTAINABILITY.map((item) => (
              <Box key={item.key}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, mb: 0.5 }}>{item.label}</Typography>
                <Stack direction="row" spacing={0.5}>
                  {([['', 'Okänt'], ['yes', 'Ja'], ['no', 'Nej']] as const).map(([value, label]) => (
                    <Button
                      key={label}
                      aria-pressed={product.sustainability[item.key] === value}
                      disabled={!canEdit || saving}
                      onClick={() => save({ sustainability: { [item.key]: value } })}
                      sx={chipButtonSx(product.sustainability[item.key] === value)}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Block>

        <Fold id="anteckningar" title={`Anteckningar (${product.notes.length})`} defaultExpanded={product.notes.length > 0}>
          <Stack spacing={1}>
            {product.notes.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga anteckningar.</Typography>}
            {[...product.notes].reverse().map((item, index) => {
              const fromExcel = item.by === 'import';
              return (
                <Box
                  key={`${item.at}-${index}`}
                  sx={fromExcel
                    ? { p: 1, borderRadius: '8px', bgcolor: stateTone.bg, border: `1px solid ${stateTone.dot}` }
                    : { borderLeft: `3px solid ${launchColors.sand}`, pl: 1.25 }}
                >
                  <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', color: launchColors.text }}>{item.text}</Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
                    {fromExcel ? 'Från Excel' : [personName(item.by, names), dateTimeText(item.at)].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              );
            })}
            {canEdit && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField size="small" fullWidth multiline label="Ny anteckning" value={note} onChange={(event) => setNote(event.target.value)} inputProps={{ maxLength: 1000 }} />
                <Button variant="outlined" disabled={!note.trim() || saving} onClick={async () => { if (await run({ type: 'addNote', text: note })) setNote(''); }}>
                  Lägg till
                </Button>
              </Stack>
            )}
          </Stack>
        </Fold>

        <Fold title={`I årskalendern (${yearbook.length})`}>
          {yearbook.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Artikelnumret finns inte i årskalendern.</Typography>
          ) : (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {yearbook.map((item) => (
                <Chip key={`${item.sheetId}-${item.rowId}`} size="small" label={`${item.chain}${item.statedWeek ? ` v${item.statedWeek}` : ''}${item.announcementDate ? ` (${item.announcementDate})` : ''}`} />
              ))}
            </Stack>
          )}
        </Fold>

        {(extra.length > 0 || product.source) && (
          <Fold title="Övrigt från Excel">
            <Stack spacing={0.5}>
              {extra.map(([key, value]) => (
                <Typography key={key} sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>{key}:</Box> {value}</Typography>
              ))}
              {product.source && (
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Från bladet {product.source.sheet}, rad {product.source.row}.</Typography>
              )}
            </Stack>
          </Fold>
        )}

        <Fold
          id="historik"
          title="Historik"
          onExpand={() => {
            if (history) return;
            alcoholApi.history(productId).then(setHistory).catch(() => setHistory([]));
          }}
        >
          {!history ? <LinearProgress /> : history.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Ingen historik.</Typography>
          ) : (
            <Stack spacing={1.25}>
              {history.map((entry) => (
                <Box key={entry.id}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{entry.summary}</Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{[personName(entry.by, names), dateTimeText(entry.at)].filter(Boolean).join(' · ')}</Typography>
                  {entry.changes.map((change) => {
                    const before = change.before || 'tomt';
                    const after = change.after || 'tomt';
                    const full = expandedChanges.includes(`${entry.id}-${change.field}`);
                    const long = before.length > 160 || after.length > 160;
                    return (
                      <Box key={change.field}>
                        <Typography sx={{ fontSize: 12, color: launchColors.text, wordBreak: 'break-word' }}>
                          {change.label}: {clipText(before, full)} → {clipText(after, full)}
                        </Typography>
                        {long && (
                          <Button size="small" onClick={() => setExpandedChanges((current) => (full ? current.filter((item) => item !== `${entry.id}-${change.field}`) : [...current, `${entry.id}-${change.field}`]))}>
                            {full ? 'Visa mindre' : 'Visa hela'}
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Stack>
          )}
        </Fold>
      </Stack>

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} message={toast || ''} />
      <TextHistoryDialog
        open={Boolean(historyAsk)}
        title={historyAsk?.title || ''}
        label={historyAsk?.label || ''}
        current={historyAsk?.current || ''}
        names={names}
        canRestore={canEdit}
        load={historyAsk?.load || productHistory}
        onRestore={(text) => {
          historyAsk?.restore(text);
          setHistoryAsk(null);
        }}
        onClose={() => setHistoryAsk(null)}
      />

      <Dialog open={Boolean(duplicateAsk)} onClose={() => setDuplicateAsk(null)}>
        <DialogTitle>Artikelnumret finns redan</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Samma artikelnummer finns på:</Typography>
          {duplicateAsk?.duplicates.map((item) => (
            <Typography key={item.id} sx={{ fontSize: 14 }}>{item.articleNumber} {item.name}</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateAsk(null)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={() => {
              const ask = duplicateAsk;
              setDuplicateAsk(null);
              if (ask) void run(ask.patch, true);
            }}
          >
            Spara ändå
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlcoholCard;
